import { computed } from 'vue'
import { freezeObject } from '@/global/utils/utils'
import RequestDetailsInfo from '@/modules/road-maintenance-module/winter-service-v2/requests/components/RequestDetailsInfo.vue'
import { useRequestFields } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/useRequestFields'
import { useRequestTabsEdit } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/useRequestTabsEdit'
import { useRequestAbnormality } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/useRequestAbnormality'
import {
  applyRequestForm,
  toRequestPayload,
  toTaskSnapshotForm,
  withRequestForm
} from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/requestData'
import { useRequestTask } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/useRequestTask'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  buildRequestEndpoint,
  REQUESTS_TRANSLATION_NAMESPACE as NS,
  REQUEST_BASIC_INFO_TAB_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/requests/config/constants'

/**
 * The basic information tab of a request. It is the tab that owns the request record
 * itself - the route tab beside it only looks at the task's streets - so this is where
 * the fetch and the save live.
 *
 * The tab carries no action buttons of its own: the screen header sits above the tab
 * strip and drives whichever tab is open (see RequestDetailsHeader).
 */
export function useRequestBasicInfo (options) {
  const { translate, requestId } = options || {}

  // Whether the vehicle or the driver can still take the request is only known once it
  // has been fetched, which is after this list is built - the fields read it from the
  // shared state the details block fills in.
  const { isVehicleBlocked, isDriverInactive } = useRequestAbnormality()

  const { rows } = useRequestFields({
    translate,
    isVehicleBlocked,
    isDriverInactive
  })
  const { setTabEdit } = useRequestTabsEdit()
  const { getRequestTask } = useRequestTask()

  const apiConfig = freezeObject({
    get: {
      module: WINTER_SERVICE_MODULE,
      route: buildRequestEndpoint(requestId)
    },
    // Sent as POST with `_method=PATCH`, which is what the update route expects.
    post: {
      module: WINTER_SERVICE_MODULE,
      route: buildRequestEndpoint(requestId)
    }
  })

  // `rows` is not just how the component finds its fields - Content.vue's
  // applyFormValuesForSend walks `tabContent[].rows[].fields[]` to build the save
  // payload, so a block that keeps its fields anywhere else saves nothing. Only the
  // vehicle and the driver are `creatable` here, so only those two are ever sent.
  const infoBlock = computed(() => ({
    component: RequestDetailsInfo,
    visible: true,
    rows: rows.value
  }))

  // The backend reports a request nested - the status, the vehicle and the driver each
  // their own object - and the fields read it flat. Everything the task contributes is
  // fetched and merged by the block itself, which is the only place that knows the task.
  const onDataReceived = (apiData) => {
    applyRequestForm(apiData?.data, translate)
  }

  /**
   * `copiedData` is only what the fields declared as creatable - the vehicle and the
   * driver, which is the whole payload: nothing else about a request is edited here.
   */
  const modifyDataBeforeSend = ({ copiedData }) => toRequestPayload(copiedData)

  // The save answers with the request as it came out of the update, and the layout puts
  // that straight back into the form - so it has to be translated the same way the
  // fetched one was.
  //
  // The task goes back in with it. Almost every field here belongs to the task behind the
  // request, which the details block fetched and merged in itself (see
  // `loadTaskSnapshot`); a save answers with the request alone, and the form it is put
  // into replaces the one the task was merged into - so without this the screen is left
  // showing a handful of request fields and nothing else. The block does not fill them in
  // again either: it re-reads the task only when the task changes, and saving a request
  // does not change which task it was made from.
  //
  // What the vehicle decides is worked out again rather than kept, because the vehicle is
  // one of the two things a save can have changed.
  const afterSave = (context, record) => {
    const task = getRequestTask()

    context?.refreshData?.({
      ...(task ? toTaskSnapshotForm(task, record?.vehicle) : {}),
      ...withRequestForm(record, translate)
    })
  }

  return computed(() => ({
    key: REQUEST_BASIC_INFO_TAB_KEY,
    title: translate(`${NS}.basic_information`),
    apiConfig,
    onDataReceived,
    afterSave,
    modifyDataBeforeSend,
    // Mirrored out so the header above the tab strip knows whether to offer edit or
    // save/cancel - it lives outside this tab's component tree.
    editUpdate: ({ edit }) => setTabEdit(REQUEST_BASIC_INFO_TAB_KEY, edit),
    tabContent: [
      infoBlock.value
    ]
  }))
}
